import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QrScanner from 'qr-scanner';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../context/NotificationContext';
import { ScanResult } from '../types';
import { couponValidator, cleanCouponCode } from '../services/couponValidation';
import LoadingSpinner from '../components/LoadingSpinner';
import WinningCard from '../components/scanner/WinningCard';
import ScanResultCard from '../components/scanner/ScanResultCard';
import { useTranslation } from 'react-i18next';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import {
  QrCodeIcon,
  BoltIcon,
  XMarkIcon,
  CameraIcon
} from '@heroicons/react/24/outline';

const Scanner: React.FC = React.memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [showWinPopup, setShowWinPopup] = useState(false);
  const [showProcessingPopup, setShowProcessingPopup] = useState(false);
  const [winAmount, setWinAmount] = useState(0);
  const [winPoints, setWinPoints] = useState(0);
  const [productName, setProductName] = useState<string>('');
  const [productPhoto, setProductPhoto] = useState<string>('');
  const [scanTimestamp, setScanTimestamp] = useState<Date>(new Date());
  const [processingSteps, setProcessingSteps] = useState<string[]>([]);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [animatedPoints, setAnimatedPoints] = useState(0);

  // Format coupon code with uppercase and one hyphen after first 3 characters
  const formatCouponCode = (input: string) => {
    const cleaned = input.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (cleaned.length <= 3) {
      return cleaned;
    } else {
      return cleaned.slice(0, 3) + '-' + cleaned.slice(3);
    }
  };

  const [cameraPermission, setCameraPermission] = useState<'unknown' | 'granted' | 'denied'>('unknown');
  const [permissionAttempts, setPermissionAttempts] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrScanner = useRef<QrScanner | null>(null);
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  useEffect(() => {
    // Track window resize for responsive design
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check

    // Prevent body scroll when scanner is open
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    // Add CSS animations to document head
    const style = document.createElement('style');
    style.textContent = `
      @keyframes scan {
        0% { top: 10%; }
        100% { top: 90%; }
      }
      
      @keyframes winPopup {
        0% { transform: scale(0.3); opacity: 0; }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); opacity: 1; }
      }
      
      @keyframes confetti {
        0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
        100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
      }
      
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }
      
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      @keyframes spin_down {
        0%   { transform: translateY(-1000%); }
        10%  { transform: translateY(-900%); }
        20%  { transform: translateY(-800%); }
        30%  { transform: translateY(-700%); }
        40%  { transform: translateY(-600%); }
        50%  { transform: translateY(-500%); }
        60%  { transform: translateY(-400%); }
        70%  { transform: translateY(-300%); }
        80%  { transform: translateY(-200%); }
        90%  { transform: translateY(-100%); }
        100% { transform: translateY(0%); }
      }
      
      @keyframes spin_up {
        0%   { transform: translateY(0%); }
        10%  { transform: translateY(-100%); }
        20%  { transform: translateY(-200%); }
        30%  { transform: translateY(-300%); }
        40%  { transform: translateY(-400%); }
        50%  { transform: translateY(-500%); }
        60%  { transform: translateY(-600%); }
        70%  { transform: translateY(-700%); }
        80%  { transform: translateY(-800%); }
        90%  { transform: translateY(-900%); }
        100% { transform: translateY(-1000%); }
      }
      
      @keyframes shineMove {
        0% { transform: translateX(-200%) rotate(25deg); }
        100% { transform: translateX(200%) rotate(25deg); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      window.removeEventListener('resize', handleResize);
      // Restore body scroll when component unmounts
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Animate points when win popup shows
  useEffect(() => {
    if (showWinPopup && winPoints > 0) {
      const duration = 1500;
      const startTime = Date.now();
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const currentValue = Math.floor(progress * winPoints);
        setAnimatedPoints(currentValue);
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setAnimatedPoints(winPoints);
        }
      };
      const timer = setTimeout(() => animate(), 300);
      return () => clearTimeout(timer);
    } else {
      setAnimatedPoints(0);
    }
  }, [showWinPopup, winPoints]);

  useEffect(() => {
    // Automatically request camera permission when page loads (with small delay)
    const timer = setTimeout(() => {
      requestCameraPermissionOnLoad();
    }, 0);

    return () => {
      clearTimeout(timer);
      stopScanning();
    };
  }, []);

  // Auto-start scanning when camera permission is granted
  useEffect(() => {
    if (cameraPermission === 'granted' && !isScanning && !manualMode) {
      // Small delay to ensure video element is ready
      const timer = setTimeout(() => {
        startScanning();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [cameraPermission, manualMode]);

  const requestCameraPermissionOnLoad = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraPermission('denied');
        return false;
      }

      // First check if permission is already granted
      if (navigator.permissions) {
        try {
          const permission = await navigator.permissions.query({ name: 'camera' as PermissionName });
          if (permission.state === 'granted') {
            setCameraPermission('granted');
            return true;
          }
        } catch (e) {
          // Continue to request permission
        }
      }

      // If not granted, automatically request permission
      setLoading(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      stream.getTracks().forEach(track => track.stop());
      setCameraPermission('granted');
      setLoading(false);
      return true;

    } catch (error) {
      setCameraPermission('denied');
      setPermissionAttempts(prev => prev + 1);
      setLoading(false);
      return false;
    }
  };

  const requestCameraPermission = async () => {
    setLoading(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      stream.getTracks().forEach(track => track.stop());
      setCameraPermission('granted');

    } catch (error) {
      setCameraPermission('denied');
      setPermissionAttempts(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const startScanning = async (cameraMode?: 'environment' | 'user') => {
    if (!videoRef.current) return;

    try {
      setLoading(true);

      // Stop any existing stream first
      if (videoRef.current.srcObject) {
        const existingStream = videoRef.current.srcObject as MediaStream;
        existingStream.getTracks().forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }

      // Initialize QrScanner
      // QrScanner.start() handles the camera stream acquisition
      qrScanner.current = new QrScanner(
        videoRef.current,
        (result: any) => {
          // Robustly handle both older string results and newer object results
          const scanText = (result && typeof result === 'object' && result.data) ? result.data : String(result);
          if (scanText) {
            handleScanResult(scanText);
          }
        },
        {
          preferredCamera: cameraMode || facingMode,
          highlightCodeOutline: false,
          maxScansPerSecond: 5,
          returnDetailedScanResult: true // Force object response in newer versions
        }
      );

      await qrScanner.current.start();
      setIsScanning(true);
      setLoading(false);

    } catch (error) {
      console.error('Camera start error:', error);
      setLoading(false);
      // Only set denied if it's a permission error, effectively QrScanner throws if permission denied
      setCameraPermission('denied');
    }
  };

  const stopScanning = () => {
    if (qrScanner.current) {
      qrScanner.current.stop();
      qrScanner.current.destroy();
      qrScanner.current = null;
    }

    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }

    setIsScanning(false);
  };

  const handleScanResult = async (data: string) => {
    if (loading || !user || !user.id || !data) return;

    setLoading(true);
    stopScanning();

    const rawData = String(data);
    const cleanedCode = cleanCouponCode(rawData);

    if (!cleanedCode) {
      setLoading(false);
      setTimeout(() => startScanning(), 100);
      return;
    }

    await processCoupon(cleanedCode);
  };

  const handleManualSubmit = async () => {
    if (!manualInput.trim() || loading || !user || !user.id) return;

    setLoading(true);
    await processCoupon(manualInput.trim());
  };

  const processCoupon = async (couponCode: string) => {
    setProcessingSteps([t('scanner.validatingCouponCode')]);
    setShowProcessingPopup(true);

    try {
      // Check if user is available
      if (!user || !user.id) {
        throw new Error(t('scanner.userNotLoaded'));
      }

      const validationResult = await couponValidator.validateCoupon(couponCode, user.id);

      if (validationResult.isValid && validationResult.coupon) {
        // Store product info from coupon
        const coupon = validationResult.coupon;
        setProductName(coupon.productName || 'SCI Paint Product');
        setScanTimestamp(new Date());

        // Try to fetch product photo if productName exists
        if (coupon.productName) {
          try {
            const productsRef = collection(db, 'products');
            const productQuery = query(productsRef, where('name', '==', coupon.productName));
            const productSnapshot = await getDocs(productQuery);
            if (!productSnapshot.empty) {
              const productData = productSnapshot.docs[0].data();
              setProductPhoto(productData.photoURL || '');
            }
          } catch (error) {
            console.error('Error fetching product photo:', error);
          }
        }

        const processResult = await couponValidator.processCouponUsage(validationResult.coupon, user);

        const scanResult: ScanResult = {
          couponCode: couponCode,
          success: processResult.success,
          cashbackEarned: processResult.cashbackEarned,
          pointsEarned: processResult.pointsEarned,
          newPointsBalance: processResult.newPointsBalance,
          newBalance: processResult.newBalance,
          message: processResult.message,
          validationSteps: [t('scanner.couponValidated'), t('scanner.processingCompleted')]
        };

        setScanResult(scanResult);

        if (processResult.success) {
          setWinAmount(processResult.cashbackEarned || 0);
          setWinPoints(processResult.pointsEarned || 0);
          setAnimatedPoints(0);
          setShowWinPopup(true);

          // Send success notification
          let notificationMessage = '';
          if (processResult.cashbackEarned && processResult.cashbackEarned > 0 && processResult.pointsEarned && processResult.pointsEarned > 0) {
            notificationMessage = `You won ₹${processResult.cashbackEarned} cashback and ${processResult.pointsEarned} points`;
          } else if (processResult.cashbackEarned && processResult.cashbackEarned > 0) {
            notificationMessage = `You won ₹${processResult.cashbackEarned} cashback`;
          } else if (processResult.pointsEarned && processResult.pointsEarned > 0) {
            notificationMessage = `You won ${processResult.pointsEarned} points`;
          } else {
            notificationMessage = t('scanner.scanSuccessful');
          }

          addNotification({
            title: t('scanner.scanSuccessful'),
            message: notificationMessage,
            type: 'success',
            time: 'Just now'
          });
        }
      } else {
        const scanResult: ScanResult = {
          couponCode: couponCode,
          success: false,
          message: validationResult.message,
          validationSteps: [t('scanner.validationFailed')],
          pointsEarned: 0
        };

        setScanResult(scanResult);
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('scanner.failedToProcess');
      setScanResult({
        success: false,
        couponCode,
        message: errorMessage,
        validationSteps: [t('scanner.validationFailed')],
        pointsEarned: 0
      });
    } finally {
      setLoading(false);
      setShowProcessingPopup(false);
    }
  };

  const resetScan = () => {
    setScanResult(null);
    setManualInput('');
    setWinPoints(0);
    if (!manualMode) {
      startScanning();
    }
  };

  const toggleFlash = async () => {
    try {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        const track = stream.getVideoTracks()[0];
        const capabilities = track.getCapabilities() as any;

        if (capabilities.torch) {
          await track.applyConstraints({
            advanced: [{ torch: !flashEnabled } as any]
          });
          setFlashEnabled(!flashEnabled);
        }
      }
    } catch (error) {
      console.error('Flash not supported');
    }
  };

  const toggleCamera = async () => {
    try {
      const newFacingMode = facingMode === 'environment' ? 'user' : 'environment';

      // Stop current scanning first
      if (isScanning) {
        stopScanning();
      }

      // Update facing mode
      setFacingMode(newFacingMode);

      // Start scanning with new camera immediately, passing the new mode
      setTimeout(() => {
        startScanning(newFacingMode);
      }, 200);
    } catch (error) {
      console.error('Camera switch failed:', error);
    }
  };

  if (!user || !user.id) {
    return <LoadingSpinner />;
  }

  // Block access if no dealer assigned
  if (!user.dealerCode) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#f3f4f6',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: '32px',
          width: '100%',
          maxWidth: '360px',
          textAlign: 'center',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#fee2e2',
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" style={{ width: '40px', height: '40px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>

          <h2 style={{
            fontSize: '24px',
            fontWeight: '800',
            color: '#1a1a1a',
            marginBottom: '12px'
          }}>
            Dealer Required
          </h2>

          <p style={{
            fontSize: '16px',
            color: '#6b7280',
            marginBottom: '32px',
            lineHeight: '1.5'
          }}>
            You must link a dealer to your account before you can scan coupons.
          </p>

          <button
            onClick={() => navigate('/profile')}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
            }}
          >
            Add Dealer Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#000',
      overflow: 'hidden'
    }}>
      {/* Full Screen Camera */}
      <video
        ref={videoRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
        playsInline
        muted
        autoPlay
      />

      {/* Scanner Overlay - Only show when actively scanning */}
      {isScanning && cameraPermission === 'granted' && (
        <>
          {/* Dark Overlay with Cut-out */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            pointerEvents: 'none',
            zIndex: 5
          }}>
            {/* Center Square Cut-out (responsive and pixel-perfect) */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              // More compact QR frame for better device compatibility
              width: 'min(60vw, 50vh, 280px)',
              height: 'min(60vw, 50vh, 280px)',
              border: '3px solid #fff',
              borderRadius: '20px',
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)'
            }}>
              {/* Corner Decorations */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '48px',
                height: '48px',
                borderTop: '5px solid #10b981',
                borderLeft: '5px solid #10b981',
                borderRadius: '20px 0 0 0'
              }} />
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '48px',
                height: '48px',
                borderTop: '5px solid #10b981',
                borderRight: '5px solid #10b981',
                borderRadius: '0 20px 0 0'
              }} />
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '48px',
                height: '48px',
                borderBottom: '5px solid #10b981',
                borderLeft: '5px solid #10b981',
                borderRadius: '0 0 0 20px'
              }} />
              <div style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '48px',
                height: '48px',
                borderBottom: '5px solid #10b981',
                borderRight: '5px solid #10b981',
                borderRadius: '0 0 20px 0'
              }} />

              {/* Scanning Line */}
              <div style={{
                position: 'absolute',
                left: '10%',
                width: '80%',
                height: '3px',
                background: 'linear-gradient(90deg, transparent, #10b981, transparent)',
                animation: 'scan 2s linear infinite',
                boxShadow: '0 0 10px #10b981'
              }} />
            </div>
          </div>

          {/* Instruction Text - Moved to Top */}
          <div style={{
            position: 'absolute',
            top: 'clamp(80px, 12vh, 120px)',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            color: 'white',
            zIndex: 10,
            padding: '0 20px'
          }}>
            <QrCodeIcon style={{ width: '40px', height: '40px', margin: '0 auto 8px', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))' }} />
            <div style={{
              fontSize: '18px',
              fontWeight: '700',
              textShadow: '0 2px 8px rgba(0,0,0,0.8)',
              marginBottom: '6px'
            }}>
              {t('scanner.scanQrCode')}
            </div>
            <div style={{
              fontSize: '13px',
              opacity: 0.9,
              textShadow: '0 2px 8px rgba(0,0,0,0.8)',
              whiteSpace: 'nowrap'
            }}>
              {t('scanner.alignQrCode')}
            </div>
          </div>
        </>
      )}

      {/* Camera Permission Overlay */}
      {cameraPermission === 'denied' && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          color: 'white',
          textAlign: 'center',
          padding: 'clamp(16px, 4vh, 24px)',
          zIndex: 20
        }}>
          {/* Text Content Above Button */}
          <div style={{ marginBottom: '40px' }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 20px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)'
            }}>
              <svg width='32' height='32' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M9 3H15L17 5H20C21.1 5 22 5.9 22 7V19C22 20.1 21.1 21 20 21H4C2.9 21 2 20.1 2 19V7C2 5.9 2.9 5 4 5H7L9 3Z' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                <circle cx='12' cy='13' r='4' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
              </svg>
            </div>
            <h2 style={{
              fontSize: 'clamp(20px, 5vw, 26px)',
              marginBottom: '12px',
              fontWeight: '800'
            }}>
              {t('scanner.enableCameraPermission')}
            </h2>
            <p style={{
              fontSize: 'clamp(14px, 4vw, 16px)',
              opacity: 0.9,
              lineHeight: '1.4',
              maxWidth: '320px',
              margin: '0 auto'
            }}>
              {t('scanner.cameraAccessRequired')}
            </p>
          </div>

          {/* Centered Button */}
          <button
            onClick={requestCameraPermission}
            disabled={loading}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '16px',
              padding: 'clamp(14px, 3.5vw, 18px) clamp(28px, 7vw, 36px)',
              fontSize: 'clamp(15px, 4vw, 17px)',
              fontWeight: '800',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)',
              transition: 'all 0.3s ease',
              transform: loading ? 'scale(0.98)' : 'scale(1)',
              marginBottom: '24px'
            }}
          >
            {loading ? t('scanner.requestingPermission') : t('scanner.enableCameraPermission')}
          </button>

          {/* Manual Permission Instructions */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '16px',
            maxWidth: '320px',
            margin: '0 auto'
          }}>
            <div style={{
              fontSize: 'clamp(13px, 3.5vw, 15px)',
              fontWeight: '600',
              marginBottom: '8px',
              color: '#10b981'
            }}>
              {permissionAttempts > 1 ? t('scanner.permissionDenied') : t('scanner.cantEnableCamera')}
            </div>
            <div style={{
              fontSize: 'clamp(12px, 3vw, 14px)',
              opacity: 0.8,
              lineHeight: '1.4'
            }}>
              {permissionAttempts > 1 ? (
                <>
                  <div style={{ marginBottom: '6px' }}>🔧 <strong>{t('scanner.manualPermissionInstructions.chrome')}</strong></div>
                  <div style={{ marginBottom: '6px' }}>🔧 <strong>{t('scanner.manualPermissionInstructions.safari')}</strong></div>
                  <div style={{ marginBottom: '6px' }}>🔧 <strong>{t('scanner.manualPermissionInstructions.firefox')}</strong></div>
                  <div>🔄 <strong>{t('scanner.manualPermissionInstructions.refresh')}</strong></div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '6px' }}>• {t('scanner.manualPermissionInstructions.goToSettings')}</div>
                  <div style={{ marginBottom: '6px' }}>• {t('scanner.manualPermissionInstructions.findPermissions')}</div>
                  <div style={{ marginBottom: '6px' }}>• {t('scanner.manualPermissionInstructions.allowAccess')}</div>
                  <div>• {t('scanner.manualPermissionInstructions.refreshPage')}</div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Top Controls */}
      {!manualMode && !showWinPopup && !scanResult && (
        <div style={{
          position: 'absolute',
          top: 'clamp(80px, 15vh, 120px)',
          left: '20px',
          right: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 100
        }}>

          {/* Flash Toggle */}
          {isScanning && (
            <button
              onClick={toggleFlash}
              style={{
                backgroundColor: flashEnabled ? 'rgba(16, 185, 129, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(10px)',
                border: flashEnabled ? '2px solid #10b981' : '2px solid rgba(255,255,255,0.3)',
                borderRadius: '50%',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: flashEnabled
                  ? '0 4px 12px rgba(16, 185, 129, 0.5)'
                  : '0 4px 12px rgba(0, 0, 0, 0.3)'
              }}
            >
              <BoltIcon style={{
                width: '28px',
                height: '28px',
                color: 'white',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
              }} />
            </button>
          )}

          {/* Camera Switch Button */}
          {isScanning && (
            <button
              onClick={toggleCamera}
              style={{
                backgroundColor: facingMode === 'user' ? 'rgba(16, 185, 129, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(10px)',
                border: facingMode === 'user' ? '2px solid #10b981' : '2px solid rgba(255,255,255,0.3)',
                borderRadius: '50%',
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: facingMode === 'user'
                  ? '0 4px 12px rgba(16, 185, 129, 0.5)'
                  : '0 4px 12px rgba(0, 0, 0, 0.3)'
              }}
            >
              <CameraIcon style={{
                width: '28px',
                height: '28px',
                color: 'white',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
              }} />
            </button>
          )}
        </div>
      )}

      {/* Bottom Manual Input Button - Always visible */}
      {!manualMode && !showWinPopup && !scanResult && (
        <div style={{
          position: 'absolute',
          bottom: 'clamp(100px, 20vh, 150px)',
          left: '20px',
          right: '20px',
          zIndex: 25
        }}>
          <button
            onClick={() => {
              stopScanning();
              setManualMode(true);
            }}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '50px',
              padding: '18px 24px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px'
            }}
          >
            <span style={{ fontSize: '20px' }}>⌨️</span>
            <span>{t('scanner.typeCodeManually')}</span>
          </button>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && !showProcessingPopup && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 30
        }}>
          <div style={{
            color: 'white',
            fontSize: '48px',
            animation: 'spin 1s linear infinite'
          }}>
            ⚙️
          </div>
        </div>
      )}

      {/* Manual Input Modal */}
      {manualMode && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9998,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '24px',
            padding: '32px',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '22px',
                fontWeight: '800',
                margin: 0
              }}>
                {t('scanner.enterCouponCode')}
              </h2>
              <button
                onClick={() => {
                  setManualMode(false);
                  setManualInput('');
                  if (cameraPermission === 'granted' && !isScanning) {
                    setTimeout(() => startScanning(), 300);
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                <XMarkIcon style={{ width: '24px', height: '24px', color: '#6b7280' }} />
              </button>
            </div>

            <input
              type="text"
              value={manualInput}
              onChange={(e) => setManualInput(formatCouponCode(e.target.value))}
              placeholder="SCI-XXXXXXXXXXX"
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '16px',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                marginBottom: '24px',
                outline: 'none',
                textAlign: 'center',
                fontWeight: '600',
                letterSpacing: '1px'
              }}
              autoFocus
            />

            <button
              onClick={handleManualSubmit}
              disabled={loading || !manualInput.trim()}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '16px',
                fontWeight: '700',
                border: 'none',
                borderRadius: '12px',
                background: loading || !manualInput.trim()
                  ? '#d1d5db'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
                cursor: loading || !manualInput.trim() ? 'not-allowed' : 'pointer',
                boxShadow: loading || !manualInput.trim()
                  ? 'none'
                  : '0 4px 12px rgba(16, 185, 129, 0.4)'
              }}
            >
              {loading ? t('scanner.processing') : t('scanner.submitCode')}
            </button>
          </div>
        </div>
      )}

      {/* Processing Popup - Animated Text Loader */}
      {showProcessingPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(20, 20, 30, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9998,
          padding: '16px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: isMobile ? '24px' : '40px',
            padding: isMobile ? '2.5rem 1.5rem' : '4rem 3rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            maxWidth: isMobile ? 'calc(100vw - 32px)' : '500px',
            width: '100%',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Animated background gradient */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 70%)',
              animation: 'pulse 3s ease-in-out infinite',
              pointerEvents: 'none'
            }}></div>
            <div style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: isMobile ? '20px' : '28px',
              height: isMobile ? '70px' : '100px',
              boxSizing: 'content-box' as const,
              padding: '0 4px',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '8px',
              gap: isMobile ? '8px' : '15px',
              width: '100%',
              overflow: 'visible'
            }}>
              {/* Left Side - Action Words */}
              <div style={{
                overflow: 'hidden',
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                flex: '1',
                minWidth: 0
              }}>
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(#ffffff 5%, transparent 30%, transparent 70%, #ffffff 95%)',
                  zIndex: 20,
                  pointerEvents: 'none' as const
                }}></div>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  color: '#64748b',
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                  animation: 'spin_down 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingRight: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingWords.connecting')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  color: '#64748b',
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                  animation: 'spin_down 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingRight: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingWords.validating')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  color: '#64748b',
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                  animation: 'spin_down 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingRight: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingWords.checking')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  color: '#64748b',
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                  animation: 'spin_down 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingRight: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingWords.processing')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  color: '#64748b',
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                  animation: 'spin_down 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingRight: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingWords.calculating')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  color: '#64748b',
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                  animation: 'spin_down 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingRight: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingWords.updating')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  color: '#64748b',
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                  animation: 'spin_down 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingRight: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingWords.verifying')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  color: '#64748b',
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                  animation: 'spin_down 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingRight: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingWords.analyzing')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  color: '#64748b',
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                  animation: 'spin_down 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingRight: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingWords.confirming')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  color: '#64748b',
                  justifyContent: 'flex-end',
                  textAlign: 'right',
                  animation: 'spin_down 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingRight: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingWords.connecting')}</span>
              </div>

              {/* Right Side - Object Words */}
              <div style={{
                overflow: 'hidden',
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                flex: '1',
                minWidth: 0
              }}>
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(#ffffff 5%, transparent 30%, transparent 70%, #ffffff 95%)',
                  zIndex: 20,
                  pointerEvents: 'none' as const
                }}></div>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  background: 'linear-gradient(45deg, #ff0080, #7928ca, #0070f3)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  animation: 'spin_up 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingLeft: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingObjects.server')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  background: 'linear-gradient(45deg, #ff0080, #7928ca, #0070f3)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  animation: 'spin_up 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingLeft: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingObjects.coupon')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  background: 'linear-gradient(45deg, #ff0080, #7928ca, #0070f3)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  animation: 'spin_up 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingLeft: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingObjects.code')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  background: 'linear-gradient(45deg, #ff0080, #7928ca, #0070f3)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  animation: 'spin_up 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingLeft: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingObjects.cashback')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  background: 'linear-gradient(45deg, #ff0080, #7928ca, #0070f3)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  animation: 'spin_up 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingLeft: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingObjects.balance')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  background: 'linear-gradient(45deg, #ff0080, #7928ca, #0070f3)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  animation: 'spin_up 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingLeft: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingObjects.wallet')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  background: 'linear-gradient(45deg, #ff0080, #7928ca, #0070f3)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  animation: 'spin_up 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingLeft: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingObjects.points')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  background: 'linear-gradient(45deg, #ff0080, #7928ca, #0070f3)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  animation: 'spin_up 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingLeft: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingObjects.transaction')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  background: 'linear-gradient(45deg, #ff0080, #7928ca, #0070f3)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  animation: 'spin_up 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingLeft: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingObjects.database')}</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  height: '100%',
                  flexShrink: 0,
                  background: 'linear-gradient(45deg, #ff0080, #7928ca, #0070f3)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  animation: 'spin_up 15s infinite cubic-bezier(0.4, 0, 0.2, 1)',
                  paddingLeft: isMobile ? '2px' : '8px',
                  fontSize: 'inherit'
                }}>{t('scanner.processingObjects.server')}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Winning Popup - Flip Card */}
      {showWinPopup && scanResult && (
        <WinningCard
          winAmount={winAmount}
          winPoints={winPoints}
          animatedPoints={animatedPoints}
          couponCode={scanResult.couponCode || ''}
          productName={productName}
          productPhoto={productPhoto}
          timestamp={scanTimestamp}
          onClose={() => {
            setShowWinPopup(false);
            resetScan();
          }}
        />
      )}

      {/* Scan Result Display */}
      {scanResult && !showWinPopup && (
        <ScanResultCard
          scanResult={scanResult}
          onReset={resetScan}
        />
      )}
    </div>
  );
});

export default Scanner;
